import { useEffect, useState } from "react";
import { getLocale, useSelector, shallowEqual } from "umi";
import moment from 'moment';
import type { SortItem } from '@/pages/dashboard/components/HeatMapTable';
import { KEY_DAYS_DEFAULT_VALUE, LANGUAGE_MAP, PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { getApiData } from "@/pages/dashboard/services/common";
import { formatAccumulation, formatFilterValues } from "@/pages/dashboard/utils/utils";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { flattenCustomDimensions } from "./_utils";

function getNumber(string: string) {
  const numStr = string?.match(/\d+/g);
  // 如果没有匹配到数字，返回一个大的默认值，使得这个项在排序时放在数组尾部
  return numStr ? parseInt(numStr?.[0]) : Infinity;
}

const useData = (
  config: any,
  formValue: any,
  filterLoading: boolean,
  tag: string,
  accumulated: string[],
  resTag?: string,
  extraParams?: Record<string, any>
) => {
  const { api } = config;
  const { tableApi } = api || {};
  const gameCode = useSelector((state: any) => {
    return state?.dashboard?.currentGame?.game_code;
  }, shallowEqual);
  const [componentData, setComponentData] = useState({
    avg: [],
    page: 1,
    rentention_days: [],
    total_record: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { overLimitBarTriggerKey, handleResponse } = useOverLimitTrigger();

  useEffect(() => {
    if (!formValue) return;
    !filterLoading && getRetentionDataFunc(KEY_DAYS_DEFAULT_VALUE, { pageIndex: 1, pageSize: 10 }, []);
  }, [formValue, tag, extraParams]);

  const getRetentionDataFunc = (keyDays: number, paging: any, sort: SortItem[]) => {
    const { rangeTime, timeType, diff, ...filters } = formValue;
    let desc = true;
    const orderBy = (sort || []).map((value) => {
      desc = value.order === 'desc';
      return value.code?.replace('__heat_', '') ?? '';
    });

    const sortedBy = orderBy?.length ? orderBy?.[0] : 'date';

    const params = {
      game_code: gameCode,
      period: PERIODTIMEOBJ?.[timeType],
      start_date: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      end_date: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      column_len: keyDays,
      retention_type: tag,
      page: paging?.pageIndex,
      page_size: paging?.pageSize,
      sorted_by: sortedBy,
      desc,
      filters: formatFilterValues(filters),
      accumulation: formatAccumulation(accumulated),
      ...(extraParams || {}),
    };
    setLoading(true);
    const locale = LANGUAGE_MAP[getLocale()];

    getApiData(`${tableApi}?language=${locale}`, params, false).then((dataList: any) => {
      setError(false);
      // const result = testData; 
      const result = flattenCustomDimensions(dataList);
      const avg: any = [];
      const page = result?.page;
      const total_record = result?.row_count;
      const rentention_days: any = [];
      result?.metrics_list?.map((item: any) => {
        const obj: any = { date: item?.date, rentention_detail: [] };
        const metricKey = resTag || 'return_users';
        obj.count = item?.metrics?.[metricKey]?.value;
        obj.dod = item?.metrics?.[metricKey]?.dod;
        obj.group_by = [];
        obj.group_by_cn = [];
        obj.group_by_en = [];
        // 指标数据处理
        const metricsList = Object.keys(item?.metrics)?.filter((v) => !v?.includes('_'))?.sort((a, b) => getNumber(a) - getNumber(b)) || [];
        metricsList?.map((w: any) => {
          const str = `${w  }:${  item?.metrics?.[w]?.value ? item?.metrics?.[w]?.value : ''
          }:${  item?.metrics?.[`retention_users_${  w}`]?.value ? item?.metrics?.[`retention_users_${  w}`]?.value : ''}`;
          obj.rentention_detail?.push(str);
        });
        // 分维度数据处理
        const groupList = accumulated?.filter((v: any) => v !== 'date');
        if (groupList?.length > 0) {
          groupList?.map((g: any) => {
            const enName = item?.dimension_name?.[`${g  }_name_en`];
            const cnName = item?.dimension_name?.[`${g  }_name_cn`];
            obj.group_by?.push(locale === 'en' ? enName : cnName);
            obj.group_by_cn?.push(cnName);
            obj.group_by_en?.push(enName);
          });
        }
        rentention_days?.push(obj);
      });
      // avg数据处理
      if (result?.aggregation?.length) {
        const avgObj = result?.aggregation?.find((v: any) => v?.type === 'avg') || { metrics: {} };
        const metricsList = Object.keys(avgObj?.metrics)?.filter((v) => !v?.includes('_'))?.sort((a, b) => getNumber(a) - getNumber(b)) || [];
        metricsList?.map((w: any) => {
          const str = `${w  }:${  avgObj?.metrics?.[w]?.value ? avgObj?.metrics?.[w]?.value : ''
          }:${  avgObj?.metrics?.[`retention_users_${  w}`]?.value ? avgObj?.metrics?.[`retention_users_${  w}`]?.value : ''}`;
          avg?.push(str);
        });
      }
      setComponentData({
        avg,
        page,
        rentention_days,
        total_record,
        front_logical_flag: dataList?.front_logical_flag,
      });
      handleResponse(dataList?.front_logical_flag);
      setLoading(false);
    }).catch(() => {
      setComponentData({
        avg: [],
        page: 1,
        rentention_days: [],
        total_record: 0,
      });
      setError(true);
      setLoading(false);
    });
  };

  return {
    getRetentionDataFunc,
    // nodata 不在此封装，因为可能是接口的个别项目nodata
    componentData,
    loading,
    error,
    overLimitBarTriggerKey,
  };
};

export default useData;